import { api } from '../../../core/utils/apiClient';
import {
  WebEnrichmentData,
  WebEnrichmentKillSwitch,
  WebEnrichmentRetryResponse,
  WebEnrichmentStatus,
  WebEnrichmentSubmission,
  WebEnrichmentUsageEntry,
} from '../types';

class WebEnrichmentService {
  async submitWebsite(
    companyId: number,
    websiteUrl: string,
    consentAcknowledged: boolean
  ): Promise<WebEnrichmentSubmission> {
    const response = await api.post(
      `/api/v1/web-enrichment/companies/${companyId}/website-enrichment/submit`,
      {
        website_url: websiteUrl,
        consent_acknowledged: consentAcknowledged,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail ||
          error?.message ||
          'Failed to submit website for enrichment'
      );
    }

    return response.json();
  }

  async getStatus(companyId: number): Promise<WebEnrichmentStatus> {
    const response = await api.get(
      `/api/v1/web-enrichment/companies/${companyId}/status`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load enrichment status'
      );
    }

    return response.json();
  }

  async getData(companyId: number): Promise<WebEnrichmentData> {
    const response = await api.get(
      `/api/v1/web-enrichment/companies/${companyId}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load web enrichment data'
      );
    }

    return response.json();
  }

  async retry(companyId: number): Promise<WebEnrichmentRetryResponse> {
    const response = await api.post(
      `/api/v1/web-enrichment/companies/${companyId}/retry`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Unable to retry enrichment'
      );
    }

    return response.json();
  }

  async getUsage(days: number = 7): Promise<WebEnrichmentUsageEntry[]> {
    const response = await api.get(
      `/api/v1/web-enrichment/admin/usage?days=${days}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load usage data'
      );
    }

    return response.json();
  }

  async getKillSwitch(): Promise<WebEnrichmentKillSwitch> {
    const response = await api.get(`/api/v1/web-enrichment/admin/kill-switch`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load kill switch status'
      );
    }

    return response.json();
  }

  async toggleKillSwitch(enabled: boolean): Promise<WebEnrichmentKillSwitch> {
    const response = await api.post(
      `/api/v1/web-enrichment/admin/kill-switch`,
      {
        enabled,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to update kill switch'
      );
    }

    return response.json();
  }

  async patchExtractedData(
    companyId: number,
    patch: Record<string, any>
  ): Promise<WebEnrichmentData> {
    const response = await api.patch(
      `/api/v1/web-enrichment/companies/${companyId}/extracted-data`,
      { data: patch }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to save web info edits'
      );
    }

    return response.json();
  }

  async acceptPendingOverride(
    companyId: number,
    field: string
  ): Promise<WebEnrichmentData> {
    const response = await api.post(
      `/api/v1/web-enrichment/companies/${companyId}/pending-overrides/${field}/accept`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to accept override'
      );
    }

    return response.json();
  }

  async discardPendingOverride(
    companyId: number,
    field: string
  ): Promise<WebEnrichmentData> {
    const response = await api.post(
      `/api/v1/web-enrichment/companies/${companyId}/pending-overrides/${field}/discard`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to discard override'
      );
    }

    return response.json();
  }

  async acceptAllPendingOverrides(
    companyId: number
  ): Promise<WebEnrichmentData> {
    const response = await api.post(
      `/api/v1/web-enrichment/companies/${companyId}/pending-overrides/accept-all`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to accept all overrides'
      );
    }

    return response.json();
  }
}

export const webEnrichmentService = new WebEnrichmentService();

